import {
  defaultLeaguesRecord,
  resolveDefaultLeagueId,
  resolveDefaultUsername,
  SAMPLE_LEAGUE_IDS,
} from '../config/leagueDefaults.js';

const STORAGE_KEY = 'fantasy_focus_leagues';
const DASHBOARD_KEY = 'fantasy_focus_dashboard';
const YAHOO_TOKEN_KEY = 'fantasy_focus_yahoo_token';
const YAHOO_REFRESH_KEY = 'fantasy_focus_yahoo_refresh';

const readStoredLeagues = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Saved leagues, falling back to the built-in default league so the app is
 * usable on first visit without running onboarding.
 */
export const loadLeagues = () => readStoredLeagues() || defaultLeaguesRecord();

export const saveLeagues = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable (e.g. private browsing quota exceeded)
  }
};

export const clearLeagues = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(YAHOO_REFRESH_KEY);
    sessionStorage.removeItem(YAHOO_TOKEN_KEY);
  } catch {
    // ignore
  }
};

// Yahoo access token lives in sessionStorage (clears when the tab closes)
export const loadYahooToken = () => {
  try {
    const raw = sessionStorage.getItem(YAHOO_TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveYahooToken = (tokenData) => {
  try {
    const payload = {
      access_token: tokenData.access_token,
      expires_at: Date.now() + (tokenData.expires_in ?? 3600) * 1000,
    };
    sessionStorage.setItem(YAHOO_TOKEN_KEY, JSON.stringify(payload));
    if (tokenData.refresh_token) {
      localStorage.setItem(YAHOO_REFRESH_KEY, tokenData.refresh_token);
    }
  } catch {
    // ignore
  }
};

export const loadYahooRefreshToken = () => {
  try {
    return localStorage.getItem(YAHOO_REFRESH_KEY) || null;
  } catch {
    return null;
  }
};

export const isYahooTokenValid = () => {
  const token = loadYahooToken();
  if (!token) return false;
  return token.expires_at > Date.now() + 60_000;
};

export const getYahooAccessToken = () => {
  const token = loadYahooToken();
  return token?.access_token || null;
};

/**
 * Returns true when at least one platform has a saved league ID / key.
 */
export const hasAnyLeague = () => {
  const leagues = loadLeagues();
  if (!leagues) return false;
  return !!(
    leagues.sleeper?.leagueId ||
    leagues.espn?.leagueId ||
    leagues.yahoo?.leagueKey
  );
};

/** ESPN league ID to work with: saved value from the rooting dashboard. */
export const getEspnLeagueId = () => readStoredLeagues()?.espn?.leagueId?.trim() || '';

/** ESPN season year for league API calls. */
export const getEspnSeason = () => {
  const saved = readStoredLeagues()?.espn?.season?.trim();
  return saved || String(new Date().getFullYear());
};

/** First ESPN team ID the user manages (comma-separated list in storage). */
export const getEspnTeamId = () => {
  const raw = readStoredLeagues()?.espn?.teamIds?.trim();
  if (!raw) return '';
  return raw.split(',')[0].trim();
};

export const getSleeperLeagueId = () =>
  readStoredLeagues()?.sleeper?.leagueId?.trim() || resolveDefaultLeagueId();

/** Sleeper username used to auto-select "your team". */
export const getSleeperUsername = () => {
  const saved = readStoredLeagues()?.sleeper?.managers?.trim();
  // The onboarding field accepts a comma-separated list; the first entry is the user.
  const first = saved ? saved.split(',')[0].trim() : '';
  return first || resolveDefaultUsername();
};

const defaultDashboardPrefs = () => ({
  username: resolveDefaultUsername(),
  leagueIds: SAMPLE_LEAGUE_IDS,
  useSampleLeagues: true,
});

const readDashboardPrefs = () => {
  try {
    const raw = localStorage.getItem(DASHBOARD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** In-week dashboard: username + selected league IDs. */
export const loadDashboardPrefs = () => readDashboardPrefs() || defaultDashboardPrefs();

export const saveDashboardPrefs = (prefs) => {
  try {
    localStorage.setItem(DASHBOARD_KEY, JSON.stringify(prefs));
  } catch {
    // Storage unavailable
  }
  // Keep the legacy leagues record in sync for draft tool / other views.
  const leagues = readStoredLeagues() || defaultLeaguesRecord();
  saveLeagues({
    ...leagues,
    sleeper: {
      ...leagues.sleeper,
      leagueId: prefs.leagueIds?.[0] || leagues.sleeper?.leagueId,
      managers: prefs.username || leagues.sleeper?.managers,
    },
  });
};
